class LTProductAnimation extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.video = this.querySelector('video');
    this.button = this.querySelector('button');
    if (!this.video || !this.button) return;
    this.visible = false;
    this.userPaused = false;
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.previewCard = this.hasAttribute('data-hover-preview') ? this.closest('.resource-card') : null;
    if (this.previewCard) {
      for (const event of ['pointerenter', 'pointerleave', 'focusin', 'focusout']) {
        this.previewCard.addEventListener(event, () => queueMicrotask(() => this.sync()), { signal });
      }
      this.closest('dialog')?.addEventListener('close', () => this.sync(), { signal });
    }
    this.video.muted = this.video.defaultMuted = true;
    this.video.playsInline = true;
    this.video.controls = false;
    this.video.addEventListener('loadeddata', () => { this.dataset.ready = ''; }, { signal });
    this.button.hidden = true;
    // No persistent overlay on the product. Keyboard users can still pause.
    this.video.tabIndex = this.previewCard ? -1 : 0;
    this.video.setAttribute('aria-label', 'Animație produs. Apasă spațiu pentru pauză sau redare.');
    this.video.addEventListener('keydown', event => {
      if (event.code !== 'Space') return;
      event.preventDefault();
      event.stopPropagation();
      if (this.video.paused) { this.userPaused = false; this.play(); }
      else { this.userPaused = true; this.video.pause(); }
    }, { signal });
    this.button.addEventListener('click', event => {
      event.preventDefault();
      event.stopPropagation();
      if (this.video.paused) { this.userPaused = false; this.play(); }
      else { this.userPaused = true; this.video.pause(); }
    }, { signal });
    const reflect = () => {
      const paused = this.video.paused;
      this.button.hidden = Boolean(this.previewCard) || !(paused && this.motion.matches);
      this.button.setAttribute('aria-label', 'Redă animația produsului');
      this.button.querySelector('[data-animation-label]').textContent = 'Redă';
    };
    this.video.addEventListener('play', reflect, { signal });
    this.video.addEventListener('pause', reflect, { signal });
    document.addEventListener('visibilitychange', () => this.sync(), { signal });
    this.motion.addEventListener('change', () => this.sync(), { signal });
    this.observer = new IntersectionObserver(entries => {
      this.visible = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.3;
      this.sync();
    }, { threshold: [0, 0.3] });
    this.observer.observe(this);
    reflect();
  }
  async play() {
    try {
      this.loadSource();
      await this.video.play();
      // An observer callback may have paused the element while play() was pending.
      if (!this.visible || document.hidden || !this.previewActive()) this.video.pause();
    }
    catch (error) {
      // Only a browser-blocked autoplay gets a play fallback, never a pause button.
      if (error.name === 'NotAllowedError' && this.visible && !this.previewCard) this.button.hidden = false;
    }
  }
  loadSource() {
    if (this.video.getAttribute('src')) return;
    const compact = this.previewCard || this.closest('card-gallery, .card-gallery') || matchMedia('(max-width: 749px)').matches;
    const target = compact ? 720 : 1080;
    const sources = Array.from(this.video.querySelectorAll('source[data-src]'))
      .map(source => ({ url: source.dataset.src, height: Number(source.dataset.height) }))
      .filter(source => source.url && source.height > 0)
      .sort((a, b) => a.height - b.height);
    // Shopify can offer 480/720/1080 or only an original; pick one, never preload all.
    const selected = sources.find(source => source.height >= target) || sources.at(-1);
    if (!selected) return;
    this.dataset.resolution = String(selected.height);
    this.video.src = selected.url;
    this.video.load();
  }
  sync() {
    if (this.visible && !document.hidden && !this.userPaused && !this.motion.matches && this.previewActive()) this.play();
    else this.video.pause();
  }
  previewActive() {
    if (!this.previewCard) return true;
    const dialog = this.closest('dialog');
    return (!dialog || dialog.open) && (this.previewCard.matches(':hover') || this.previewCard.matches(':focus-within'));
  }
  disconnectedCallback() {
    this.observer?.disconnect();
    this.controller?.abort();
    this.video?.pause();
    this.controller = null;
  }
}
if (!customElements.get('lt-product-animation')) customElements.define('lt-product-animation', LTProductAnimation);
